/**
 * Scale + center signature stroke data so the WHOLE signature fits a canvas of
 * the given CSS size, preserving aspect ratio.
 *
 * Why: v-perfect-signature stores stroke points as ABSOLUTE canvas pixel
 * coordinates and `fromData()` replays them 1:1 with no rescaling, while the
 * submission view/PDF renders the signature box at whatever the container width
 * is now. A signature drawn on a larger canvas therefore spills past the smaller
 * render canvas and is clipped by the `overflow:hidden` wrapper (confirmed on
 * prod 2026-07-01: canvas 184x94 vs signature extent X~324 / Y~194). Fitting the
 * strokes to the canvas shows the whole signature. Display-only — the stored data
 * is untouched, so existing submissions render correctly retroactively.
 *
 * @param {Array} strokes  array of strokes; each stroke = array of [x, y, pressure?]
 * @param {number} targetW canvas CSS width  (px)
 * @param {number} targetH canvas CSS height (px)
 * @param {number} padding inner padding (px)
 * @returns {Array} a NEW rescaled strokes array (or the input unchanged if empty/invalid)
 *
 * KEEP IN SYNC: cypress/node-tests/fit-strokes-to-canvas.test.js loads this file.
 */
export default function fitStrokesToCanvas(strokes, targetW, targetH, padding = 6) {
    if (!Array.isArray(strokes) || strokes.length === 0) return strokes;
    if (!(targetW > 0) || !(targetH > 0)) return strokes;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, count = 0;
    for (const stroke of strokes) {
        if (!Array.isArray(stroke)) continue;
        for (const p of stroke) {
            if (!Array.isArray(p) || typeof p[0] !== 'number' || typeof p[1] !== 'number') continue;
            if (p[0] < minX) minX = p[0];
            if (p[0] > maxX) maxX = p[0];
            if (p[1] < minY) minY = p[1];
            if (p[1] > maxY) maxY = p[1];
            count++;
        }
    }
    if (count === 0) return strokes;

    const bw = Math.max(maxX - minX, 1);
    const bh = Math.max(maxY - minY, 1);
    const availW = Math.max(targetW - 2 * padding, 1);
    const availH = Math.max(targetH - 2 * padding, 1);
    const scale = Math.min(availW / bw, availH / bh);          // preserve aspect, never overflow
    const offX = (targetW - bw * scale) / 2 - minX * scale;    // center horizontally
    const offY = (targetH - bh * scale) / 2 - minY * scale;    // center vertically

    return strokes.map(stroke =>
        Array.isArray(stroke)
            ? stroke.map(p =>
                (Array.isArray(p) && typeof p[0] === 'number' && typeof p[1] === 'number')
                    ? (p.length > 2 ? [p[0] * scale + offX, p[1] * scale + offY, p[2]]
                                    : [p[0] * scale + offX, p[1] * scale + offY])
                    : p)
            : stroke
    );
}
